/**
 * SignioValidation - A utility for validating Signio form fields
 * This module implements validation rules from the backend's SignioFieldsSerializer
 */
import { validate_id } from "@/utils/validations.js";

// Available field options (choices) for select fields
const signio_fields = {
  // Period options
  get_repayment_period_available: () => [
    "6 Months",
    "12 Months",
    "18 Months",
    "24 Months",
    "30 Months",
    "36 Months",
    "42 Months",
    "48 Months",
    "54 Months",
    "60 Months",
    "72 Months",
  ],

  // Vehicle options
  get_article_type_available: () => [
    "Motor Vehicle",
    "Light Duty Vehicle",
    "MotorCycle",
    "Boats",
    "Caravans And Camping Equipment",
    "OffRoad",
    "Trailer",
    "Jet-Ski",
  ],

  get_article_color_available: () => [
    "Not Selected",
    "White",
    "Black",
    "Red",
    "Blue",
    "Yellow",
    "Silver",
    "Green",
    "Purple",
    "Gold",
    "Grey",
    "Orange",
  ],

  get_extra_list: () => [
    "ABS CONVERSION",
    "ACCOUSTIC PARKING",
    "ADCOVER",
    "AIR BAGS",
    "AIRCONDITIONER",
    "ALARM",
    "AMG CONVERSION",
    "AUTO ARMOUR",
    "AUTO STAR WARR",
    "AUTO TR/MISSION",
    "BAKKIE LINER",
    "BLUE TOOTH CELL KIT",
    "BODY",
    "BODY VARIATION",
    "BOOT SPOILER",
    "BOSE SOUND",
    "BULL BAR",
    "BUSHBAR",
    "CANOPY",
    "CAR CARE VOUCHER",
    "CAR TELEPHONE",
    "CAR TRACK",
    "CARPETS",
    "CD PLAYER",
    "CELL PHONE",
    "CELL PREP",
    "CENTRAL LOCKING",
    "COLOUR CODING",
    "CRUISE CONTROL",
    "DATA DOT",
    "DEALER MAINT.",
    "DIFF LOCK",
    "ELECTR. MIRRORS",
    "ELECTR. WINDOWS",
    "EXHAUST MODIFIC",
    "FIRE EXTINGUISHER",
    "FOG LIGHTS",
    "FUEL INJECTION",
    "FUEL TANK",
    "GEAR LOCK",
    "HEADLAMP COVERS",
    "IMMOBILISER",
    "INITIATION FEE FINANCED",
    "INITIATION FEE CASH",
    "LEATHER TRIM",
    "LICENCE FEE",
    "MAG WHEELS",
    "METALLIC PAINT",
    "MOTORCARE",
    "MOTORITE",
    "MOUSE BAR",
    "MUD FLAPS",
    "NUMBER PLATES",
    "OEM Extra",
    "ON THE ROAD COSTS",
    "ONBOARD COMPUT.",
    "OTHER EXTRAS",
    "PAINT PROTECTION",
    "PERMIT",
    "POWER STEERING",
    "PROCESSING FEE CASH",
    "PROCESSING FEE FINANCED",
    "PROSEAL",
    "RADIO",
    "RADIO/TAPEDECK",
    "ROLL BAR",
    "ROOF RACKS",
    "RUBBER MATS",
    "RUBBERISING",
    "RUNFLAT TYRES",
    "RUNNING BOARD",
    "RUST INHIBITOR",
    "SAFE",
    "SATELLITE NAVIGATION SYSTEM",
    "SCOTCH GUARD",
    "SEAT",
    "SEAT BELTS",
    "SEAT COVERS",
    "SERV AND DELIVERY",
    "SIGNWRITING",
    "SKYTRAX",
    "SLIDING WINDOWS",
    "SPEAKERS",
    "SPEEDO CRUISE",
    "SPOT LAMPS",
    "STEP",
    "STONE GUARDS",
    "STRIPING",
    "SUN ROOF",
    "SUSP. MODIFIC.",
    "TORMEAUR COVER",
    "TOW-BAR",
    "TRIM VARIATION",
    "TYRES",
    "TYRON SAFE.BAND",
    "UNDERSEAL",
    "UPHOLSTERY CONV",
    "VALET",
    "WHEEL HUB",
    "WINCH",
    "WINDOW BREAK-IN PROTECTION",
    "WINDOW ID",
    "WINDOW TINTING",
    "XENON LIGHTS",
    "Audio/Media",
    "Navigation",
    "Nudgebar",
    "Park distance control",
    "Spare wheel lock",
    "Spoilers",
    "Administrative Charges",
    "AUTO ARMOUR",
    "MAINT PLAN NO VAT",
    "XTENDED MAINTENANCE PLAN",
    "PARK DISTANCE CONTROL - PARK CONTROL",
    "PARK DISTANCE CONTROL - PARKTRONIC",
    "PARK DISTANCE CONTROL - PARKING AID SENSORS",
    "PARK DISTANCE CONTROL - PARKING PACK",
    "REVERSE CAMERA - OPTICAL PARKING",
    "REVERSE CAMERA - REARVIEW CAMERA",
    "REVERSE CAMERA - REAR VIEW CAMERA AND HITCH ASSIST",
    "NUDGE BAR",
    "LED BAR",
    "LED LIGHTS",
    "ELECTRIC TOW BAR",
    "ADMIN FEE",
    "ALLOY WHEELS",
    "ARMADILLO",
    "BONNET PROTECTOR",
    "DRIVER ASSIST",
    "LAZER LIGHTS",
    "OPTION PACK",
    "REVERSE SENSORS",
    "SCUFF PLATES",
    "SPARE WHEEL LOCK",
    "STARTER PACK",
    "TENT",
    "TRACKING DEVICE",
    "TRALI",
  ],

  // Person options
  get_customer_titles_available: () => [
    "Not Selected",
    "Advocate",
    "Doctor",
    "Mr",
    "Miss",
    "Mrs",
    "Reverend",
    "Professor",
  ],

  get_ethnic_group_available: () => [
    "Not Selected",
    "Asian",
    "Black",
    "Coloured",
    "White",
  ],

  get_marital_status_available: () => [
    "Not Selected",
    "Divorced",
    "Married",
    "Single",
    "Widow/er",
  ],

  get_marital_contract_available: () => [
    "Antenuptual Contract",
    "In Community of property",
    "Traditional Marriage",
    "Married in COP (Chapter 2 & 3)",
    "Married in COP (not Chapter 2 & 3)",
    "Foreign/Custom",
  ],

  get_relative_relation_available: () => [
    "Not Selected",
    "Aunt",
    "Brother",
    "Brother in Law",
    "Cousin",
    "Daughter",
    "Father",
    "Father in Law",
    "Friend",
    "Grandfather",
    "Grandmother",
    "Husband",
    "Mother",
    "Mother in Law",
    "Sister",
    "Sister in Law",
    "Son",
    "Uncle",
    "Wife",
  ],

  get_preferred_contact_method_available: () => [
    "Not Selected",
    "Cell phone",
    "Email",
    "Home phone",
    "Postal",
    "Work phone",
  ],

  get_residence_permit_type_available: () => [
    "Not Selected",
    "Asylum Seeker",
    "Permanent Resident",
    "Refugee",
    "Study Permit",
    "Work Permit",
    "Temporary Resident",
  ],

  // Banking options
  get_bond_lenders_available: () => [
    "Not Selected",
    "ABN AMRO BANK",
    "ABSA BANK",
    "AFRICAN BANK",
    "BANK WINDHOEK",
    "BIDVEST BANK",
    "BOE BANK (NEDBANK)",
    "CAPITEC BANK",
    "CITIBANK",
    "FBC FIDELITY BANK LTD",
    "FIRST NATIONAL BANK",
    "GRINDROD BANK",
    "HABIB OVERSEAS BANK",
    "HBZ BANK",
    "INVESTEC BANK LIMITED",
    "MERCANTILE BANK",
    "MFC",
    "MTN BANKING (Standard Bank)",
    "NBS (Nedbank)",
    "NEDBANK",
    "NEDBANK (Lesotho)",
    "NEDBANK (Namibia)",
    "NEDBANK (Swaziland)",
    "OTHER",
    "PEP BANK (NEDBANK)",
    "PERMANENT BANK",
    "RESERVE BANK",
    "SA BANK OF ATHENS",
    "SA POST OFFICE",
    "STANDARD BANK",
    "STANDARD BANK (SWAZILAND)",
    "STANDARD CHARTERED BANK (20TWENTY)",
    "STANDARD LESOTHO BANK LIMITED",
    "UBAN BANK",
    "Wesbank",
  ],

  get_bank_account_type_available: () => [
    "Not Selected",
    "Cheque",
    "Savings",
    "Transmission",
  ],

  get_bank_names_available: function () {
    return this.get_bond_lenders_available();
  },

  get_payment_date_available: () => [
    "01",
    "02",
    "03",
    "04",
    "05",
    "06",
    "07",
    "08",
    "09",
    "10",
    "11",
    "12",
    "13",
    "14",
    "15",
    "16",
    "17",
    "18",
    "19",
    "20",
    "21",
    "22",
    "23",
    "24",
    "25",
    "26",
    "27",
    "28",
    "29",
    "30",
    "31",
  ],

  get_statement_delivery_method_available: () => [
    "Electronic Statement",
    "Normal Printed Statement",
    "Consolidated Printed Statement",
    "No Statement",
  ],

  // Employment options
  get_customer_occupation_available: () => [
    "Not Selected",
    "Able Seaman",
    "Accommodation Occupations",
    "Accountant/Auditor",
    "Actor or Actress",
    "Admin Assistant",
    "Admin Clerk",
    "Admin Manager",
    "Admiral",
    "Advocate",
    "Air Transport Occupations",
    "Ammunition Seeker",
    "Anaesthetist",
    "Appraiser",
    "Apprentice",
    "Architect",
    "Area Manager",
    "Artisan",
    "Artist",
    "Assembler",
    "Assessor",
    "Assistant",
    "Assistant Manager",
    "Assistant to the Trades",
    "Attorney",
    "Auctioneer",
    "Author",
    "Baker",
    "Bank Employee",
    "Bank Teller",
    "Barmaid",
    "Barman / Bartender",
    "Beautician",
    "Biologist",
    "Boilermaker",
    "Bombardier",
    "Book Maker",
    "Bookkeeper",
    "Bricklayer",
    "Brigadier",
    "Broker",
    "Builder",
    "Building Contractor",
    "Building Inspector",
    "Building Painter",
    "Business Consultant",
    "Butcher",
    "Buyer",
    "Cabinet Maker",
    "Cabinet Minister",
    "Cable Joiner",
    "Candidate Officer",
    "Captain",
    "Carpenter / Joiner",
    "Carpenter or Joiner",
    "Cashier",
    "Casting Worker",
    "Catering Occupations",
    "Chairman of Company",
    "Chartered Accountant",
    "Chefs",
    "Chemist",
    "Chief Petty Officer",
    "Chiropracter",
    "Civil Servant",
    "Co-ordinator",
    "Colonel",
    "Commander",
    "Commandore",
    "Commissioner",
    "Computer Operator",
    "Computer Programmer",
    "Computer System Analyst",
    "Constable",
    "Consultants",
    "Cook",
    "Copywriter",
    "Corporal",
    "Credit Collector or Personnel",
    "Dance Instructor",
    "Data Puncher",
    "Dean",
    "Debtors / Creditors Clerk",
    "Dental Assistant",
    "Dentist",
    "Designer",
    "Diplomat - not RSA Citizen",
    "Director",
    "District Manager",
    "Doctor / General Practitioner",
    "Draughtsman",
    "Driver - Vehicle",
    "Electrician",
    "Engineer",
    "Ensign",
    "Estate Agent",
    "Extension Officer - Agricultural",
    "Factory Worker",
    "Farm Foreman",
    "Farm Worker",
    "Farmer (Private Capacity)",
    "Farmer Parttime",
    "Farmer ie Grower",
    "Financial Advisors",
    "Fireman",
    "Fisherman or Hunter",
    "Fisherman/Hunter",
    "Fitter And Turner",
    "Flight Sargeant",
    "Foreman",
    "Forensic Analyst",
    "Foundry Worker",
    "Funeral Director",
    "General",
    "General Manager - Company",
    "Geologist",
    "Glazier",
    "Government Employees",
    "Graphic Designer",
    "Grinder",
    "Gynecologist",
    "Hairdresser / Barber",
    "Head of Technikon/College/School",
    "Headmaster",
    "Housekeeper",
    "Housewife",
    "IT/IT Technician",
    "Inspector",
    "Inspector of Schools",
    "Insurance Agent",
    "Insurance Broker",
    "Insurance Consultant",
    "Interior Decorator",
    "Iron Worker",
    "Jeweller",
    "Journalist",
    "Judge",
    "Labourer / Unskilled Labourer",
    "Labourer or Unskilled Labourer",
    "Laundryman",
    "Lay preacher",
    "Leading Seaman",
    "Lecturer",
    "Legal Professions Other",
    "Librarian",
    "Lieutenant",
    "Lieutenant Colonel",
    "Lieutenant Commander",
    "Lieutenant General",
    "Lumberman",
    "Machine Operator",
    "Machinist",
    "Major",
    "Major General",
    "Manager",
    "Managing Director",
    "Market Checker",
    "Mason",
    "Mechanic",
    "Medic",
    "Medical Doctor",
    "Member of Parliament",
    "Member of Provincial Council",
    "Metal Worker",
    "Meter Reader",
    "Midshipman",
    "Millwright",
    "Mine Worker",
    "Minister",
    "Missionary",
    "Model",
    "Moulder",
    "Municipal Servant",
    "Musician",
    "Nurse",
    "Nursery Worker",
    "Nursing Sister",
    "Office Worker",
    "Officer",
    "Operator/Production Worker",
    "Optician",
    "Other Occupations Not Mentioned",
    "Packer",
    "Painter",
    "Painter - Buildings",
    "Paramedics",
    "Part time / Contract Worker",
    "Pensioner",
    "Personnel Occupations",
    "Petty Officer",
    "Pharmacist",
    "Photographer",
    "Pilot",
    "Pipefitter",
    "Plasterer",
    "Plumber",
    "Police Officer",
    "Porter",
    "Postal Services Occupations",
    "President",
    "Priest",
    "Prison Officer",
    "Private",
    "Production Control",
    "Production Line Worker",
    "Professor",
    "Prospector",
    "Psychiatrist",
    "Psychologist",
    "Quality Control",
    "Quality Surveyor",
    "Radio Announcer",
    "Rail Transport Occupation",
    "Rear Admiral",
    "Receptionist",
    "Rector",
    "Regional Manager",
    "Repair Man",
    "Reporter",
    "Researchers",
    "Retired",
    "Rigger / Scaffolder",
    "Road Transport Occupations",
    "Safety Officer",
    "Salesman",
    "Salesman/Marketer",
    "Sargeant",
    "Sargeant Major",
    "Scholar",
    "Scientist",
    "Sculptor",
    "Sea Transport Occupations",
    "Seaman",
    "Second Bombardier",
    "Second Corporal",
    "Second Lieutenant",
    "Secretary",
    "Security Guard",
    "Security Officer",
    "Self Employed(Professional)",
    "Self Employed(non-professional)",
    "Senator",
    "Senior Executive Consultant",
    "Senior Superintendent",
    "Serviceman",
    "Shopfitter",
    "Singer",
    "Slaughterman",
    "Social Worker",
    "Soldier",
    "Sole Owner",
    "Sole Owner - Individual(retail bank)",
    "Sole Owner(Business Bank)",
    "Specialist",
    "Sport Occupations",
    "Spray Painter",
    "Staff Sargeant",
    "Stage Artist",
    "Steel Worker",
    "Steeplejack",
    "Stock Controller",
    "Student",
    "Sub-Lieutenant",
    "Superintendent",
    "Supervisor",
    "Surgeon",
    "Surveyor",
    "Taxi Owner / Driver",
    "Teacher",
    "Technician",
    "Telecommunication Occupation",
    "Telephonist",
    "Therapeutist",
    "Traffic Officer",
    "Train Driver",
    "Transport Contractor",
    "Typist",
    "Unemployed",
    "Veterinarian",
    "Vice Admiral",
    "Vice Corporal",
    "Waiter",
    "Warrent Officer",
    "Welder",
    "Zoologist",
  ],

  get_employer_type_available: () => [
    "AGRICULTURE",
    "BUILDING AND CONSTRUCTION",
    "BUSINESS SERVICES",
    "COMMERCE",
    "COMMUNITY SERVICE",
    "EDUCATIONAL SERVICES - PRIVATE",
    "ELECTRICITY,GAS AND WATER",
    "ENTERTAINMENT/SPORTS/RECREATION",
    "ESCOM",
    "FINANCIAL",
    "GOVERNMENT",
    "MANUFACTURING",
    "MEDICAL SERVICES - PRIVATE",
    "MINING",
    "NON-PROFIT ORGANISATIONS",
    "PERSONAL SERVICES",
    "RETAIL",
    "TELKOM",
    "TRANSPORT/STORAGE/COMMUNICATION",
    "UNKNOWN",
  ],

  get_employment_type_available: () => [
    "Not Selected",
    "Permanent",
    "Contractual",
    "Part time",
    "Student",
    "Self employed",
    "Pensioner",
    "Unemployed",
  ],

  get_employment_level_available: () => [
    "Not Selected",
    "Senior Management",
    "Management",
    "Supervisor",
    "Skilled Worker",
    "Semi-Skilled Worker",
    "Unskilled Worker",
    "Junior Position",
  ],

  get_customer_type_available: () => [
    "Not Selected",
    "Private Individual",
    "Self Employed(Non-professional)",
    "Self Employed(Professional)",
  ],

  // Other options
  get_source_of_income_available: () => [
    "Salary",
    "Pension",
    "Investments",
    "Policy",
    "Retirement Annuity",
    "Donation/Gift",
    "Inheritance",
  ],

  get_deposit_source_available: () => ["Trade-in", "Cash", "Other"],

  get_take_a_break_month_available: () => [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ],

  get_mfc_client_type_available: () => ["Type A", "Type B", "Other"],
  get_country_available: () => [
    "SOUTH AFRICA",
    "AFGANISTAN",
    "ALBANIA",
    "ALGERIA",
    "AMERICAN SAMOA",
    "ANDORRA",
    "ANGOLA",
    "ANTIGUA (WEST INDIES)",
    "ARGENTINA",
    "AUSTRALIA",
    "AUSTRIA",
    "AZORES",
    "BAHAMAS",
    "BAHRAIN",
    "BANGLADESH",
    "BARBADOS",
    "BELEARIC ISLAND (SPAIN)",
    "BELGIUM",
    "BELIZE",
    "BENIN",
    "BERMUDA",
    "BHUTAN",
    "BOLIVIA",
    "BOTSWANA",
    "BRAZIL",
    "BRUNEI",
    "BULGARIA",
    "BURUNDI",
    "CAMEROON (UNITED REPUBLIC OF)",
    "CANADA",
    "CAPE VERDE",
    "CAYMAN ISLAND (WEST INDIES)",
    "CENTRAL AFRICAN REPUBLIC",
    "CHAD",
    "CHILE",
    "CHINA",
    "CHRISTMAS ISLAND",
    "COCOS (KEELING) ISLAND",
    "COLUMBIA",
    "COMOROS",
    "CONGO (PEOPLE DEMOCRATIC REPUBLIC OF)",
    "COOK ISLAND",
    "COSTA RICA",
    "CUBA",
    "CYPRUS",
    "CZECHOSLOVAKIA",
    "DENMARK",
    "DJIBOUTI",
    "DOMINICA (WEST INDIES)",
    "DOMINICAN REPUBLIC",
    "EAST TIMOR",
    "ECUADOR",
    "EGYPT",
    "EL SALVADOR",
    "EQUATORIAL GUINEA",
    "ETHIOPIA",
    "FALKLAND ISLAND",
    "FEAROE ISLAND",
    "FIJI",
    "FINLAND",
    "FRANCE",
    "FRENCH GUIANA",
    "FRENCH POLYNESIA",
    "GABON",
    "GAMBIA",
    "GERMANY (DEMOCRATIC)",
    "GHANA",
    "GIBRALTAR",
    "GRANADA (WEST INDIES)",
    "GREECE",
    "GREENLAND",
    "GUADELOUPE (MARIE-GALANTE AND ST BARTHELEM)",
    "GUAM",
    "GUATEMALA",
    "GUINEA",
    "GUINEA-BISSAU",
    "GUYANA",
    "HAITI",
    "HONDURAS",
    "HONG KONG",
    "HUNGARY",
    "ICELAND",
    "INDIA",
    "INDONESIA",
    "IRAN",
    "IRAQ",
    "IRELAND",
    "ISREAL",
    "ITALY",
    "JAMAICA",
    "JAPAN",
    "JORDAN",
    "KENYA",
    "KIRIBATI",
    "KOREA (NORTH DEMOCRATIC PEOPLE REPUBLIC)",
    "KOREA (SOUTH DEMOCRATIC PEOPLE REPUBLIC)",
    "KUWAIT",
    "LAO (PEOPLE DEMOCRATIC REPUBLIC)",
    "LEBANON",
    "LESOTHO",
    "LIBERIA",
    "LIBYA",
    "LIECHTENSTEIN",
    "LUXEMBORG",
    "MACAU",
    "MADAGASCAR",
    "MALAWI (REPUBLIC OF)",
    "MALAYSIA",
    "MALDIVES",
    "MALI (REPUBLIC OF)",
    "MALTA-GOZO",
    "MARTINIQUE (FRENCH WEST INDIES)",
    "MAURITANIA",
    "MAURITIUS",
    "MEXICO",
    "MONACO",
    "MONGOLIA",
    "MONTSERRAT (WEST INDIES)",
    "MOROCCO",
    "MOZAMBIQUE",
    "NAMIBIA",
    "NAURU ISLAND",
    "NEPAL",
    "NETHERLANDS",
    "NETHERLANDS ANTILLES",
    "NEW CALEDONIA",
    "NEW ZEALAND",
    "NICARAGUA",
    "NIGER",
    "NIGERIA",
    "NIUE ISLAND",
    "NORFOLK ISLAND",
    "NORWAY",
    "OMAN",
    "PAKISTAN",
    "PALAU ISLANDS",
    "PANAMA",
    "PAPUA NEW GUINEA",
    "PARAGUAY",
    "PERU",
    "PHILLIPINES",
    "POLAND",
    "PORTUGAL",
    "PUERTO RICO",
    "QATAR",
    "REUNION",
    "ROMANIA",
    "RWANDA",
    "SAMOA",
    "SAN MARINO",
    "SAO TOME AND PRINCIPE",
    "SAUDI ARABIA",
    "SENEGAL",
    "SEYCHELLES",
    "SIERRA LEONNE",
    "SINGAPORE",
    "SOLOMON ISLANDS",
    "SOMALIA",
    "SPAIN",
    "SRI LANKA (CEYLON)",
    "ST HELENA",
    "ST KITTS-NEVAS ANGUILLA",
    "ST LUCIA",
    "SURINAM REPUBLIC",
    "SWAZI HOMELAND",
    "SWEDEN",
    "SWITZERLAND",
    "SYRIAN ARAB REPUBLIC",
    "TAIWAN",
    "TANZANIA (UNITED REPUBLIC OF)",
    "THAILAND",
    "TOGO (REPUBLIC OF)",
    "TOKELAU ISLANDS",
    "TONGA",
    "TRINIDAD AND TOBAGO",
    "TUNISIA",
    "TURKEY",
    "TURKS AND CAICOS ISLANDS",
    "TUVALU ISLANDS",
    "UGANDA",
    "UKRANIAN USSR",
    "UNITED ARAB EMIRATES",
    "UNITED KINGDOM",
    "UNITED STATES VIRGIN ISLANDS",
    "URUGUAY",
    "USA",
    "VANUATU ISLAND",
    "VENEZUELA",
    "VIETNAM",
    "WALLIS FUTUNA ISLANDS",
    "WESTERN SAHARA",
    "YEMEN",
    "YUGOSLAVIA",
    "ZAMBIA",
    "ZIMBABWE",
  ],

  // Additional field options based on the table
  get_id_type_available: () => ["South African Valid ID", "Passport"],

  get_id_verified_available: () => ["Yes", "No"],

  get_drivers_license_verified_available: () => ["Yes", "No"],

  get_article_condition_available: () => ["New", "Used"],

  get_article_use_available: () => ["Taxi", "Business", "Private"],

  get_kilometer_category_available: () => [
    "Not Selected",
    "High",
    "Medium",
    "Low",
  ],

  get_regtrack_indicator_available: () => ["Not Selected", "Yes", "No"],

  get_premium_vendor_available: () => ["Not Selected", "Yes", "No"],

  get_agreement_type_available: () => [
    "Installment Sale Agreement",
    "Lease Agreement",
  ],

  get_payment_frequency_available: () => [
    "Monthly",
    "Annually",
    "Quarterly",
    "Bi-Annually",
  ],

  get_rate_indicator_available: () => ["Not Selected", "Fixed", "Linked"],

  get_deal_type_available: () => ["Direct", "Discounted"],

  get_finance_initiation_fees_available: () => ["Yes", "No"],

  get_advance_available: () => ["Yes", "No"],

  get_shock_absorber_available: () => ["Yes", "No"],

  get_tcm_indicator_available: () => ["Yes", "No"],

  get_preferred_language_available: () => ["English", "Afrikaans"],

  get_gender_available: () => ["Not Selected", "Male", "Female"],

  get_graduate_available: () => ["Yes", "No"],

  get_mobile_type_available: () => ["Contract", "Pay-as-you-go"],

  get_work_phone_type_available: () => ["Land line", "Cell"],

  get_foreign_tax_obligation_available: () => ["Not Selected", "Yes", "No"],

  get_multiple_nationalities_available: () => ["Not Selected", "Yes", "No"],

  get_south_african_citizen_available: () => ["Not Selected", "Yes", "No"],

  get_same_as_res_available: () => [
    "Not Selected",
    "Use Address Below",
    "Use Residential Address",
  ],

  get_owner_tenant_lodger_available: () => [
    "Owner - Bonded",
    "Owner - Bond Free",
    "Tenant",
    "Lodger",
  ],

  get_joint_bond_available: () => ["Yes", "No"],

  get_residential_owner_available: () => [
    "Not Selected",
    "Self",
    "Both",
    "Spouse",
    "Other",
  ],

  get_spouse_id_type_available: () => ["Valid Id", "Passport", "Not Selected"],

  get_bank_employee_available: () => ["Yes", "No"],

  get_absa_employee_available: () => ["Yes", "No"],

  get_liability_available: () => ["Yes", "No"],

  get_debt_review_available: () => ["Not Selected", "Yes", "No"],

  get_debt_counselling_available: () => ["Not Selected", "Yes", "No"],

  get_administration_order_available: () => ["Not Selected", "Yes", "No"],

  get_debt_admin_available: () => ["Not Selected", "Yes", "No"],

  get_judgement_available: () => ["Not Selected", "Yes", "No"],

  get_debt_dispute_available: () => ["Not Selected", "Yes", "No"],

  get_sequestration_available: () => ["Not Selected", "Yes", "No"],

  get_payment_method_available: () => ["Cash", "Debit Order"],

  get_settle_existing_available: () => ["Yes", "No"],

  get_bank_account_status_available: () => ["Active", "Inactive"],

  get_valid_bank_account_available: () => ["Yes", "No"],
};

// Validation rules for Signio fields
const validation_rules = {
  // --- Applicant Details ---
  customerFirstName: {
    required: true,
    min_length: 3,
    max_length: 28,
    error_msg: "First name must be between 3 and 28 characters.",
  },
  customerMiddleName: {
    required: false,
    min_length: 3,
    max_length: 27,
    error_msg: "Middle name must be between 3 and 27 characters.",
  },
  customerSurname: {
    required: true,
    min_length: 3,
    max_length: 55,
    error_msg: "Surname must be between 3 and 55 characters.",
  },
  customerIdType: {
    required: false,
    allowed_values: ["South African Valid ID", "Passport", "Not Selected", ""],
    error_msg: "Invalid ID type selected.",
  },
  customerIdNumber: {
    required: true,
    min_length: 13,
    max_length: 13,
    validator: (value, form_data) => {
      if (form_data.customerIdType === "South African Valid ID") {
        if (!value || value.length !== 13 || validate_id(value) !== true) {
          return "ID Number must be a 13-digit numeric value for South African IDs.";
        }
      }
      return null;
    },
  },
  customerDateOfBirth: {
    required: true,
    min_length: 8,
    max_length: 8,
    validator: (value) => {
      if (value) {
        // Check if it's exactly 8 digits
        if (value.length !== 8 || !/^\d+$/.test(value)) {
          return "Customer Data of Birth must be in YYYYMMDD format (8 digits).";
        }

        // Extract year, month, day
        const year = parseInt(value.substring(0, 4));
        const month = parseInt(value.substring(4, 6));
        const day = parseInt(value.substring(6, 8));

        // Validate year range (reasonable range)
        if (year < 1900 || year > new Date().getFullYear() + 100) {
          return (
            "Customer Data of Birth year must be between 1900 and " +
            (new Date().getFullYear() + 100) +
            "."
          );
        }

        // Validate month
        if (month < 1 || month > 12) {
          return "Customer Data of Birth month must be between 01 and 12.";
        }

        // Validate day
        if (day < 1 || day > 31) {
          return "Customer Data of Birth day must be between 01 and 31.";
        }

        // Create a date object to check if the date is valid
        const date = new Date(year, month - 1, day);
        if (
          date.getFullYear() !== year ||
          date.getMonth() !== month - 1 ||
          date.getDate() !== day
        ) {
          return "Customer Data of Birth id invalid. Please check the day, month, and year.";
        }
      }
      return null;
    },
  },

  dateMarried: {
    required: false,
    min_length: 8,
    max_length: 8,
    validator: (value) => {
      if (value) {
        // Check if it's exactly 8 digits
        if (value.length !== 8 || !/^\d+$/.test(value)) {
          return "Date Married must be in YYYYMMDD format (8 digits).";
        }

        // Extract year, month, day
        const year = parseInt(value.substring(0, 4));
        const month = parseInt(value.substring(4, 6));
        const day = parseInt(value.substring(6, 8));

        // Validate year range (reasonable range)
        if (year < 1900 || year > new Date().getFullYear() + 100) {
          return (
            "Date Married year must be between 1900 and " +
            (new Date().getFullYear() + 100) +
            "."
          );
        }

        // Validate month
        if (month < 1 || month > 12) {
          return "Date Married month must be between 01 and 12.";
        }

        // Validate day
        if (day < 1 || day > 31) {
          return "Date Married day must be between 01 and 31.";
        }

        // Create a date object to check if the date is valid
        const date = new Date(year, month - 1, day);
        if (
          date.getFullYear() !== year ||
          date.getMonth() !== month - 1 ||
          date.getDate() !== day
        ) {
          return "Date Married is invalid. Please check the day, month, and year.";
        }
      }
      return null;
    },
  },
  spouseDateOfBirth: {
    required: false,
    min_length: 8,
    max_length: 8,
    validator: (value) => {
      if (value) {
        // Check if it's exactly 8 digits
        if (value.length !== 8 || !/^\d+$/.test(value)) {
          return "Spouse Date of Birth must be in YYYYMMDD format (8 digits).";
        }

        // Extract year, month, day
        const year = parseInt(value.substring(0, 4));
        const month = parseInt(value.substring(4, 6));
        const day = parseInt(value.substring(6, 8));

        // Validate year range (reasonable range)
        if (year < 1900 || year > new Date().getFullYear() + 100) {
          return (
            "Spouse Date of Birth year must be between 1900 and " +
            (new Date().getFullYear() + 100) +
            "."
          );
        }

        // Validate month
        if (month < 1 || month > 12) {
          return "Spouse Date of Birth month must be between 01 and 12.";
        }

        // Validate day
        if (day < 1 || day > 31) {
          return "Spouse Date of Birth day must be between 01 and 31.";
        }

        // Create a date object to check if the date is valid
        const date = new Date(year, month - 1, day);
        if (
          date.getFullYear() !== year ||
          date.getMonth() !== month - 1 ||
          date.getDate() !== day
        ) {
          return "Spouse Date of Birth is invalid. Please check the day, month, and year.";
        }
      }
      return null;
    },
  },

  // --- Nationality and Tax Details ---
  isForeignOrTaxObligation: {
    required: false,
    allowed_values: ["Not Selected", "Yes", "No"],
    error_msg:
      "Please select Yes, No, or Not Selected for foreign or tax obligation.",
  },
  isMultipleNationalities: {
    required: false,
    allowed_values: ["Not Selected", "Yes", "No"],
    error_msg:
      "Please select Yes, No, or Not Selected for multiple nationalities.",
  },
  wasSouthAfricanCitizen: {
    required: false,
    allowed_values: ["Not Selected", "Yes", "No"],
    error_msg:
      "Please select Yes, No, or Not Selected for previous South African citizenship.",
  },
  countryCitizenship: {
    required: false,
    allowed_values: signio_fields.get_country_available(),
    error_msg: "Invalid country of citizenship selected.",
  },
  residentialAddressCountry: {
    required: false,
    allowed_values: signio_fields.get_country_available(),
    error_msg: "Invalid residential address country selected.",
  },
  mfcClientType: {
    required: false,
    allowed_values: [
      "Private Individual",
      "Self Employed(Non-professional)",
      "Self Employed(Professional)",
    ],
    error_msg: "Invalid client type selected.",
  },

  // --- Vehicle Details ---
  articleType: {
    required: false,
    allowed_values: signio_fields.get_article_type_available(),
    error_msg: "Invalid article type selected.",
  },
  mmCode: {
    required: false,
    validator: (value, form_data) => {
      const restricted_types = [
        "Boats",
        "Caravans And Camping Equipment",
        "OffRoad",
        "Trailer",
        "Jet-Ski",
      ];

      if (
        restricted_types.includes(form_data.articleType) &&
        value !== "88888888"
      ) {
        return `mmCode must be '88888888' for article type '${form_data.articleType}'.`;
      }
      return null;
    },
  },
  yearOfFirstRegistration: {
    required: false,
    validator: (value) => {
      if (
        value !== null &&
        value !== undefined &&
        (isNaN(value) || value < 1000 || value > 9999)
      ) {
        return "Year must be in YYYY format.";
      }
      return null;
    },
  },
  registrationNumber: {
    required: false,
    min_length: 3,
    max_length: 8,
    error_msg: "Registration number must be between 3 and 8 characters.",
  },
  vehicleEngineSize: {
    required: false,
    validator: (value) => {
      if (
        value !== null &&
        value !== undefined &&
        (isNaN(value) || value < 0 || value > 9999)
      ) {
        return "Engine size must be up to 4 digits.";
      }
      return null;
    },
  },
  vehicleNumberOfSeats: {
    required: false,
    validator: (value) => {
      if (
        value !== null &&
        value !== undefined &&
        (isNaN(value) || value < 0 || value > 99)
      ) {
        return "Number of seats must be up to 2 digits.";
      }
      return null;
    },
  },

  // --- Finance Details ---
  repaymentPeriod: {
    required: false,
    allowed_values: signio_fields.get_repayment_period_available(),
    error_msg: "Invalid repayment period selected.",
  },
  interestRate: {
    required: false,
    validator: (value) => {
      if (value !== null && value !== undefined) {
        const num_value = parseFloat(value);
        if (isNaN(num_value) || num_value < 0 || num_value > 99.99) {
          return "Interest rate must be between 0.00 and 99.99.";
        }
      }
      return null;
    },
  },
  residualValue: {
    required: false,
    validator: (value) => {
      if (value !== null && value !== undefined) {
        const num_value = parseFloat(value);
        if (isNaN(num_value) || num_value < 0 || num_value > 99.99) {
          return "Residual value percentage must be between 0.00 and 99.99.";
        }
      }
      return null;
    },
  },

  // --- Personal Details ---
  customerMobilePhoneNumber: {
    required: true,
    min_length: 10,
    max_length: 10,
    validator: (value) => {
      if (value && (value.length !== 10 || !/^\d+$/.test(value))) {
        return "Mobile number must be 10 digits.";
      }
      return null;
    },
  },
  customerHomePhoneNumber: {
    required: true,
    min_length: 10,
    max_length: 10,
    validator: (value) => {
      if (value && (value.length !== 10 || !/^\d+$/.test(value))) {
        return "Home phone number must be 10 digits.";
      }
      return null;
    },
  },
  customerWorkPhoneNumber: {
    required: true,
    min_length: 10,
    max_length: 10,
    validator: (value) => {
      if (value && (value.length !== 10 || !/^\d+$/.test(value))) {
        return "Work phone number must be 10 digits.";
      }
      return null;
    },
  },
  customerEmail: {
    required: true,
    max_length: 80,
    validator: (value) => {
      if (value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
        return "Invalid email format.";
      }
      return null;
    },
  },
  customerPreferredEmailAddress: {
    required: false,
    max_length: 80,
    validator: (value) => {
      if (value && value.length > 80)
        return "Preferred email address max 80 characters.";
      if (value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value))
        return "Invalid email format.";
      return null;
    },
  },
  customerAlternateEmailAddress: {
    required: false,
    max_length: 81,
    validator: (value) => {
      if (value && value.length > 81)
        return "Alternate email address max 81 characters.";
      if (value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value))
        return "Invalid email format.";
      return null;
    },
  },

  // --- Residential Details ---
  customerResidentialAddressLine1: {
    required: true,
    max_length: 50,
    error_msg: "Address line 1 must be max 50 characters.",
  },
  customerResidentialAddressLine2: {
    required: false,
    max_length: 50,
    error_msg: "Address line 2 must be max 50 characters.",
  },
  customerResidentialAddressPostalCode: {
    required: false,
    max_length: 30,
    error_msg: "Postal code must be max 30 characters.",
  },

  // --- Spouse Details ---
  spouseFirstNames: {
    required: false,
    min_length: 3,
    max_length: 40,
    error_msg: "Spouse first name must be between 3 and 40 characters.",
  },
  spouseMiddleName: {
    required: false,
    min_length: 3,
    max_length: 40,
    error_msg: "Spouse middle name must be between 3 and 40 characters.",
  },
  spouseSurname: {
    required: false,
    max_length: 60,
    error_msg: "Spouse surname must be max 60 characters.",
  },
  spousePhoneNumber: {
    required: false,
    min_length: 10,
    max_length: 10,
    validator: (value) => {
      if (value && (value.length !== 10 || !/^\d+$/.test(value))) {
        return "Spouse phone number must be 10 digits.";
      }
      return null;
    },
  },

  // --- Banking Details ---
  customerBankAccountNumber: {
    required: false,
    max_length: 21,
    error_msg: "Bank account number must be max 21 characters.",
  },
  accountNumberToSettle: {
    required: false,
    max_length: 20,
    error_msg: "Account number to settle must be max 20 characters.",
  },
  accountNumberToSettle2: {
    required: false,
    max_length: 20,
    error_msg: "Account number to settle 2 must be max 20 characters.",
  },
  customerBankAccountHolder: {
    required: false,
    max_length: 60,
    error_msg: "Bank account holder max 60 characters.",
  },
  customerBankAccountBranchName: {
    required: false,
    max_length: 40,
    error_msg: "Bank branch name max 40 characters.",
  },

  // --- Relative Details ---
  relativeFirstNames: {
    required: true,
    min_length: 3,
    max_length: 40,
    error_msg: "Relative first name must be 3-40 characters.",
  },
  relativeSurname: {
    required: true,
    max_length: 60,
    error_msg: "Relative surname max 60 characters.",
  },
  relativeRelation: {
    required: true,
    allowed_values: signio_fields.get_relative_relation_available(),
    error_msg: "Invalid relative relation selected.",
  },
  relativePreferredContactMethod: {
    required: true,
    allowed_values: ["Not Selected", "Cellphone", "Home", "Work"],
    error_msg: "Invalid preferred contact method for relative.",
  },
  relativeCellphone: {
    required: true,
    max_length: 10,
    validator: (value) => {
      if (value && value.length > 10)
        return "Relative cellphone max 10 digits.";
      return null;
    },
  },
  relativePhoneNumber: {
    required: true,
    max_length: 10,
    validator: (value) => {
      if (value && value.length > 10)
        return "Relative phone number max 10 digits.";
      return null;
    },
  },
  relativeWorkNumber: {
    required: true,
    max_length: 10,
    validator: (value) => {
      if (value && value.length > 10)
        return "Relative work number max 10 digits.";
      return null;
    },
  },
  relativeAddressLine1: {
    required: true,
    max_length: 50,
    error_msg: "Relative address line 1 max 50 characters.",
  },
  relativeAddressLine2: {
    required: false,
    max_length: 50,
    error_msg: "Relative address line 2 max 50 characters.",
  },
  relativeAddressPostalCode: {
    required: false,
    max_length: 30,
    error_msg: "Relative postal code max 30 characters.",
  },

  // --- Statement ---
  customerStatementDeliveryMethod: {
    required: false,
    allowed_values: signio_fields.get_statement_delivery_method_available(),
    error_msg: "Invalid statement delivery method.",
  },

  // --- Add more validation rules for other fields as needed ---
  customerTitle: {
    required: false,
    allowed_values: signio_fields.get_customer_titles_available(),
    error_msg: "Invalid title selected.",
  },
  customerRaceEthnicGroup: {
    required: false,
    allowed_values: signio_fields.get_ethnic_group_available(),
    error_msg: "Invalid ethnic group selected.",
  },
  customerNationality: {
    required: false,
    allowed_values: signio_fields.get_country_available(),
    error_msg: "Invalid nationality selected.",
  },
  customerMaritalStatus: {
    required: false,
    allowed_values: signio_fields.get_marital_status_available(),
    error_msg: "Invalid marital status selected.",
  },
  customerMaritalContract: {
    required: false,
    allowed_values: signio_fields.get_marital_contract_available(),
    error_msg: "Invalid marital contract selected.",
  },
  customerPreferredContactMethod: {
    required: false,
    allowed_values: signio_fields.get_preferred_contact_method_available(),
    error_msg: "Invalid preferred contact method.",
  },
  countryOfBirth: {
    required: false,
    allowed_values: signio_fields.get_country_available(),
    error_msg: "Invalid country of birth selected.",
  },
  bondLenders: {
    required: false,
    allowed_values: signio_fields.get_bond_lenders_available(),
    error_msg: "Invalid bond lender selected.",
  },
  residentialOwner: {
    required: false,
    allowed_values: ["Not Selected", "Self", "Both", "Spouse", "Other"],
    error_msg: "Invalid residential owner selected.",
  },
  ownerTenantLodger: {
    required: false,
    allowed_values: ["Owner - Bonded", "Owner - Bond Free", "Tenant", "Lodger"],
    error_msg: "Invalid owner/tenant/lodger value.",
  },
  sameAsRes: {
    required: false,
    allowed_values: [
      "Not Selected",
      "Use Address Below",
      "Use Residential Address",
    ],
    error_msg: "Invalid postal address selection.",
  },
  jointHomeloanIndicator: {
    required: false,
    allowed_values: ["Yes", "No"],
    error_msg: "Invalid joint homeloan indicator.",
  },
  jointBondPercentage: {
    required: false,
    validator: (value) => {
      if (value !== null && value !== undefined) {
        const num_value = parseFloat(value);
        if (isNaN(num_value) || num_value < 0 || num_value > 100) {
          return "Joint bond percentage must be between 0 and 100.";
        }
      }
      return null;
    },
  },
  customerPeriodAtCurrentAddressYears: {
    required: false,
    validator: (value) => {
      if (
        value !== null &&
        value !== undefined &&
        (isNaN(value) || value < 0 || value > 99)
      ) {
        return "Years at current address must be between 0 and 99.";
      }
      return null;
    },
  },
  customerPeriodAtCurrentAddressMonths: {
    required: false,
    validator: (value) => {
      if (
        value !== null &&
        value !== undefined &&
        (isNaN(value) || value < 0 || value > 11)
      ) {
        return "Months at current address must be between 0 and 11.";
      }
      return null;
    },
  },
  customerPeriodAtPreviousAddressYears: {
    required: false,
    validator: (value) => {
      if (
        value !== null &&
        value !== undefined &&
        (isNaN(value) || value < 0 || value > 99)
      ) {
        return "Years at previous address must be between 0 and 99.";
      }
      return null;
    },
  },
  customerPeriodAtPreviousAddressMonths: {
    required: false,
    validator: (value) => {
      if (
        value !== null &&
        value !== undefined &&
        (isNaN(value) || value < 0 || value > 11)
      ) {
        return "Months at previous address must be between 0 and 11.";
      }
      return null;
    },
  },
  customerLevelOfEducation: {
    required: false,
    allowed_values: [
      "Not Selected",
      "PRIMARY SCHOOL",
      "SOME HIGH SCHOOL",
      "COMPLETED MATRIC",
      "DIPLOMA",
      "BACHELORS DEGREE",
      "HONOURS OR HIGHER",
    ],
    error_msg: "Invalid education level selected.",
  },
  RetrenchmentNotifyInd: {
    required: false,
    allowed_values: ["Not Selected", "Yes", "No"],
    error_msg: "Invalid retrenchment notification status selected.",
  },

  // --- Vehicle Details Additional Fields ---
  articleCondition: {
    required: false,
    allowed_values: signio_fields.get_article_condition_available(),
    error_msg: "Invalid article condition selected.",
  },
  articleUse: {
    required: false,
    allowed_values: signio_fields.get_article_use_available(),
    error_msg: "Invalid article use selected.",
  },
  make: {
    required: false,
    error_msg: "Invalid make value.",
  },
  vehicleDescription: {
    required: false,
    error_msg: "Invalid vehicle description.",
  },
  retailValue: {
    required: false,
    validator: (value) => {
      if (value !== null && value !== undefined) {
        const num_value = parseFloat(value);
        if (
          isNaN(num_value) ||
          num_value < 0 ||
          !/^\d+(\.\d{2})?$/.test(value)
        ) {
          return "Retail value must be a positive decimal number with two decimal places.";
        }
      }
      return null;
    },
  },
  purchasePriceIncludingVat: {
    required: false,
    validator: (value) => {
      if (value !== null && value !== undefined) {
        const num_value = parseFloat(value);
        if (
          isNaN(num_value) ||
          num_value < 0 ||
          !/^\d+(\.\d{2})?$/.test(value)
        ) {
          return "Purchase price must be a positive decimal number with two decimal places.";
        }
      }
      return null;
    },
  },
  kilometerReading: {
    required: false,
    validator: (value) => {
      if (value !== null && value !== undefined) {
        const num_value = parseInt(value);
        if (isNaN(num_value) || num_value < 0 || value.length > 7) {
          return "Kilometer reading must be a positive number up to 7 digits.";
        }
      }
      return null;
    },
  },
  articleColour: {
    required: false,
    allowed_values: signio_fields.get_article_color_available(),
    error_msg: "Invalid article color selected.",
  },
  kilometerCategory: {
    required: false,
    allowed_values: signio_fields.get_kilometer_category_available(),
    error_msg: "Invalid kilometer category selected.",
  },
  chassisNo: {
    required: false,
    validator: (value) => {
      if (value && !/^[A-Z0-9]*$/.test(value)) {
        return "Chassis number must contain only uppercase alphanumeric characters.";
      }
      return null;
    },
  },
  engineNumber: {
    required: false,
    validator: (value) => {
      if (value && !/^[A-Z0-9]*$/.test(value)) {
        return "Engine number must contain only uppercase alphanumeric characters.";
      }
      return null;
    },
  },
  serialNumber: {
    required: false,
    validator: (value) => {
      if (value && !/^[A-Z0-9]*$/.test(value)) {
        return "Serial number must contain only uppercase alphanumeric characters.";
      }
      return null;
    },
  },
  stockNumber: {
    required: false,
    validator: (value) => {
      if (value && !/^[A-Z0-9]*$/.test(value)) {
        return "Stock number must contain only uppercase alphanumeric characters.";
      }
      return null;
    },
  },
  regtrackIndicator: {
    required: false,
    allowed_values: signio_fields.get_regtrack_indicator_available(),
    error_msg: "Invalid REGTRACK indicator selected.",
  },

  // --- Finance Details Additional Fields ---
  agreementType: {
    required: false,
    allowed_values: signio_fields.get_agreement_type_available(),
    error_msg: "Invalid agreement type selected.",
  },
  paymentFrequency: {
    required: false,
    allowed_values: signio_fields.get_payment_frequency_available(),
    error_msg: "Invalid payment frequency selected.",
  },
  rateIndicator: {
    required: false,
    allowed_values: signio_fields.get_rate_indicator_available(),
    error_msg: "Invalid rate indicator selected.",
  },
  purchasePrice: {
    required: false,
    validator: (value) => {
      if (value !== null && value !== undefined) {
        const num_value = parseFloat(value);
        if (
          isNaN(num_value) ||
          num_value < 0 ||
          !/^\d+(\.\d{2})?$/.test(value)
        ) {
          return "Financed amount must be a positive decimal number with two decimal places.";
        }
      }
      return null;
    },
  },
  deposit: {
    required: false,
    validator: (value) => {
      if (value !== null && value !== undefined) {
        const num_value = parseFloat(value);
        if (
          isNaN(num_value) ||
          num_value < 0 ||
          !/^\d+(\.\d{2})?$/.test(value)
        ) {
          return "Deposit must be a positive decimal number with two decimal places.";
        }
      }
      return null;
    },
  },
  dealType: {
    required: false,
    allowed_values: signio_fields.get_deal_type_available(),
    error_msg: "Invalid deal type selected.",
  },
  schemeCodeWesbank: {
    required: false,
    validator: (value) => {
      if (value && value.length > 15) {
        return "Wesbank scheme code must not exceed 15 digits.";
      }
      return null;
    },
  },
  schemeCodeMFC: {
    required: false,
    validator: (value) => {
      if (value && value.length > 16) {
        return "MFC scheme code must not exceed 16 digits.";
      }
      return null;
    },
  },
  sourceOfDeposit: {
    required: false,
    allowed_values: signio_fields.get_deposit_source_available(),
    error_msg: "Invalid deposit source selected.",
  },
  financeInitiationFees: {
    required: false,
    allowed_values: signio_fields.get_finance_initiation_fees_available(),
    error_msg: "Invalid finance initiation fees selection.",
  },
  advance: {
    required: false,
    allowed_values: signio_fields.get_advance_available(),
    error_msg: "Invalid advance payment selection.",
  },
  takeABreakMonth: {
    required: false,
    allowed_values: signio_fields.get_take_a_break_month_available(),
    error_msg: "Invalid take a break month selected.",
  },
  shockAbsorberWesbank: {
    required: false,
    allowed_values: signio_fields.get_shock_absorber_available(),
    error_msg: "Invalid shock absorber selection.",
  },
  TCMIndicator: {
    required: false,
    allowed_values: signio_fields.get_tcm_indicator_available(),
    error_msg: "Invalid TCM indicator selection.",
  },

  // --- Additional Personal Details Fields ---
  customerPreferredLanguage: {
    required: false,
    allowed_values: signio_fields.get_preferred_language_available(),
    error_msg: "Invalid preferred language selected.",
  },
  customerGender: {
    required: true,
    allowed_values: signio_fields.get_gender_available(),
    error_msg: "Invalid gender selected.",
  },
  customerGraduate: {
    required: false,
    allowed_values: signio_fields.get_graduate_available(),
    error_msg: "Invalid graduate status selected.",
  },
  mobileType: {
    required: false,
    allowed_values: signio_fields.get_mobile_type_available(),
    error_msg: "Invalid mobile type selected.",
  },
  customerWorkPhoneNumberType: {
    required: false,
    allowed_values: signio_fields.get_work_phone_type_available(),
    error_msg: "Invalid work phone type selected.",
  },

  // --- Banking Details Additional Fields ---
  customerBankAccountBank: {
    required: false,
    allowed_values: signio_fields.get_bank_names_available(),
    error_msg: "Invalid bank selected.",
  },
  customerBankAccountType: {
    required: false,
    allowed_values: signio_fields.get_bank_account_type_available(),
    error_msg: "Invalid account type selected.",
  },
  customerSalaryDay: {
    required: false,
    allowed_values: signio_fields.get_payment_date_available(),
    error_msg: "Invalid payment date selected.",
  },
  validBankAccount: {
    required: false,
    allowed_values: signio_fields.get_valid_bank_account_available(),
    error_msg: "Please indicate if you have a valid bank account.",
  },
  customerBankAccountStatus: {
    required: false,
    allowed_values: signio_fields.get_bank_account_status_available(),
    error_msg: "Invalid bank account status selected.",
  },
  paymentMethod: {
    required: false,
    allowed_values: signio_fields.get_payment_method_available(),
    error_msg: "Invalid payment method selected.",
  },
  settleExistingInstallment: {
    required: false,
    allowed_values: signio_fields.get_settle_existing_available(),
    error_msg: "Invalid settle existing installment selection.",
  },
  bankNameAccToSettle: {
    required: false,
    allowed_values: signio_fields.get_bank_names_available(),
    error_msg: "Invalid bank selected for settlement.",
  },

  // --- Spouse Additional Fields ---
  spouseIdType: {
    required: false,
    allowed_values: signio_fields.get_spouse_id_type_available(),
    error_msg: "Invalid spouse ID type selected.",
  },
  spouseIdNo: {
    required: false,
    min_length: 13,
    max_length: 13,
    validator: (value) => {
      if (value && !/^\d{13}$/.test(value)) {
        return "Spouse ID number must be 13 digits.";
      }
      return null;
    },
  },

  // --- Passport Details ---
  customerPassportDateOfIssue: {
    required: false,
    min_length: 8,
    max_length: 8,
    validator: (value) => {
      if (value) {
        if (value.length !== 8 || !/^\d+$/.test(value)) {
          return "Passport issue date must be in YYYYMMDD format (8 digits).";
        }

        const year = parseInt(value.substring(0, 4));
        const month = parseInt(value.substring(4, 6));
        const day = parseInt(value.substring(6, 8));

        if (year < 1900 || year > new Date().getFullYear()) {
          return "Invalid passport issue year.";
        }
        if (month < 1 || month > 12) {
          return "Invalid passport issue month.";
        }
        if (day < 1 || day > 31) {
          return "Invalid passport issue day.";
        }

        const date = new Date(year, month - 1, day);
        if (
          date.getFullYear() !== year ||
          date.getMonth() !== month - 1 ||
          date.getDate() !== day
        ) {
          return "Invalid passport issue date.";
        }
      }
      return null;
    },
  },
  passportValidToDate: {
    required: false,
    min_length: 8,
    max_length: 8,
    validator: (value) => {
      if (value) {
        if (value.length !== 8 || !/^\d+$/.test(value)) {
          return "Passport expiry date must be in YYYYMMDD format (8 digits).";
        }

        const year = parseInt(value.substring(0, 4));
        const month = parseInt(value.substring(4, 6));
        const day = parseInt(value.substring(6, 8));

        if (year < new Date().getFullYear()) {
          return "Passport expiry date cannot be in the past.";
        }
        if (month < 1 || month > 12) {
          return "Invalid passport expiry month.";
        }
        if (day < 1 || day > 31) {
          return "Invalid passport expiry day.";
        }

        const date = new Date(year, month - 1, day);
        if (
          date.getFullYear() !== year ||
          date.getMonth() !== month - 1 ||
          date.getDate() !== day
        ) {
          return "Invalid passport expiry date.";
        }
      }
      return null;
    },
  },
  customerResidencePermitType: {
    required: false,
    allowed_values: signio_fields.get_residence_permit_type_available(),
    error_msg: "Invalid residence permit type selected.",
  },
  customerResidencePermitNumber: {
    required: false,
    max_length: 13,
    error_msg: "Permit number must not exceed 13 characters.",
  },
  customerResidencePermitCountryOfIssue: {
    required: false,
    allowed_values: signio_fields.get_country_available(),
    error_msg: "Invalid permit country of issue selected.",
  },
  customerResidencePermitDateOfIssue: {
    required: false,
    min_length: 8,
    max_length: 8,
    validator: (value) => {
      if (value) {
        if (value.length !== 8 || !/^\d+$/.test(value)) {
          return "Permit issue date must be in YYYYMMDD format (8 digits).";
        }

        const year = parseInt(value.substring(0, 4));
        const month = parseInt(value.substring(4, 6));
        const day = parseInt(value.substring(6, 8));

        if (year < 1900 || year > new Date().getFullYear()) {
          return "Invalid permit issue year.";
        }
        if (month < 1 || month > 12) {
          return "Invalid permit issue month.";
        }
        if (day < 1 || day > 31) {
          return "Invalid permit issue day.";
        }

        const date = new Date(year, month - 1, day);
        if (
          date.getFullYear() !== year ||
          date.getMonth() !== month - 1 ||
          date.getDate() !== day
        ) {
          return "Invalid permit issue date.";
        }
      }
      return null;
    },
  },
  customerResidencePermitExpiryDate: {
    required: false,
    min_length: 8,
    max_length: 8,
    validator: (value) => {
      if (value) {
        if (value.length !== 8 || !/^\d+$/.test(value)) {
          return "Permit expiry date must be in YYYYMMDD format (8 digits).";
        }

        const year = parseInt(value.substring(0, 4));
        const month = parseInt(value.substring(4, 6));
        const day = parseInt(value.substring(6, 8));

        if (year < new Date().getFullYear()) {
          return "Permit expiry date cannot be in the past.";
        }
        if (month < 1 || month > 12) {
          return "Invalid permit expiry month.";
        }
        if (day < 1 || day > 31) {
          return "Invalid permit expiry day.";
        }

        const date = new Date(year, month - 1, day);
        if (
          date.getFullYear() !== year ||
          date.getMonth() !== month - 1 ||
          date.getDate() !== day
        ) {
          return "Invalid permit expiry date.";
        }
      }
      return null;
    },
  },
  customerWorkContractExpiryDate: {
    required: false,
    min_length: 8,
    max_length: 8,
    validator: (value) => {
      if (value) {
        if (value.length !== 8 || !/^\d+$/.test(value)) {
          return "Work contract expiry date must be in YYYYMMDD format (8 digits).";
        }

        const year = parseInt(value.substring(0, 4));
        const month = parseInt(value.substring(4, 6));
        const day = parseInt(value.substring(6, 8));

        if (year < new Date().getFullYear()) {
          return "Work contract expiry date cannot be in the past.";
        }
        if (month < 1 || month > 12) {
          return "Invalid work contract expiry month.";
        }
        if (day < 1 || day > 31) {
          return "Invalid work contract expiry day.";
        }

        const date = new Date(year, month - 1, day);
        if (
          date.getFullYear() !== year ||
          date.getMonth() !== month - 1 ||
          date.getDate() !== day
        ) {
          return "Invalid work contract expiry date.";
        }
      }
      return null;
    },
  },
};

/**
 * Validate a single field value against its validation rules
 *
 * @param {string} key - Field key/name
 * @param {*} value - Field value
 * @param {Object} form_data - The complete form data object
 * @returns {string|null} Error message if validation fails, null if valid
 */
function validate_field(key, value, form_data) {
  const rules = validation_rules[key];

  // If no rules defined for this field, consider it valid
  if (!rules) {
    return null;
  }

  // Check if required
  if (
    rules.required &&
    (value === null || value === undefined || value === "")
  ) {
    return `${key} is required.`;
  }

  // If value is empty and not required, it's valid
  if (value === null || value === undefined || value === "") {
    return null;
  }

  // Check min length
  if (
    rules.min_length !== undefined &&
    typeof value === "string" &&
    value.length < rules.min_length
  ) {
    return (
      rules.error_msg ||
      `${key} must be at least ${rules.min_length} characters.`
    );
  }

  // Check max length
  if (
    rules.max_length !== undefined &&
    typeof value === "string" &&
    value.length > rules.max_length
  ) {
    return (
      rules.error_msg ||
      `${key} must be at most ${rules.max_length} characters.`
    );
  }

  // Check allowed values
  if (
    rules.allowed_values !== undefined &&
    !rules.allowed_values.includes(value)
  ) {
    return rules.error_msg || `${key} has an invalid value.`;
  }

  // Custom validator function
  if (rules.validator !== undefined) {
    return rules.validator(value, form_data);
  }

  return null;
}

/**
 * Validate all form fields in the form_data object
 *
 * @param {Object} form_data - Object containing all form field values
 * @returns {Array} Array of error objects with { key, error } format
 */
function validate_form(form_data) {
  const errors = [];

  // Validate all fields based on their rules
  Object.keys(form_data).forEach((key) => {
    const value = form_data[key];
    const error_msg = validate_field(key, value, form_data);

    if (error_msg) {
      errors.push({
        key,
        error: error_msg,
      });
    }
  });

  return errors;
}

/**
 * Handle API errors from saving the form and format them for display
 *
 * @param {Object} api_errors - Error object returned from the API
 * @returns {Array} Array of error objects with { key, error } format
 */
function handle_save_errors(api_errors) {
  const errors = [];

  // Process API error response
  if (typeof api_errors === "object" && api_errors !== null) {
    Object.keys(api_errors).forEach((key) => {
      const error_messages = api_errors[key];
      // Handle both string and array error messages
      if (Array.isArray(error_messages)) {
        error_messages.forEach((message) => {
          errors.push({ key, error: message });
        });
      } else if (typeof error_messages === "string") {
        errors.push({ key, error: error_messages });
      }
    });
  } else if (typeof api_errors === "string") {
    // For generic error messages
    errors.push({ key: "general", error: api_errors });
  }

  return errors;
}

export { signio_fields, validate_field, validate_form, handle_save_errors };
